import React, {useState} from "react";
import {useDeskingContext} from "../../contexts/DeskingContext";
import {SkeletonLoader} from "../../components/SkeletonLoader";
import classnames from "classnames";
import Tooltip from "@mui/material/Tooltip";
import {useModalsStore} from "../../stores/modalsStore";
import {NO_TIER, PROGRAM_SWITCH_CLEARED, hasProgramOverride, normalizeTier, programName, quotePassesCreditScore} from "../../utils/DeskingHelper";
import {copyQuoteLeaseSummaryHTMLTooltip} from "../../utils/CopyQuoteTooltip";
import {useThemeStore} from "../../stores/themeStore";
import {ConfirmDialog} from "../CrmNext/PersonDetailPage/ConfirmDialog";

export const DeskingTableRowCell = ({ dealId, quote, isPurchase, term, tier, isLowestPayment, showLender, creditScore }) => {
    const {
        createDeal,
        createDealResponse,
        updateDeal,
        updateDealResponse,
        deskingState,
        onSave,
        pricing_id,
        selectedTerm,
        setSelectedTerm,
        selectedLender,
        setSelectedLender,
        selectedTier,
        setSelectedTier,
        selectedProgramCode,
        setSelectedProgramCode,
        dealState,
    } = useDeskingContext()

    const closeModalById = useModalsStore((state) => state.closeModalById)
    const isDark = useThemeStore((state) => state.theme) === 'dark'
    const [isConfirmingSwitch, setIsConfirmingSwitch] = useState(false)

    const getLeadId = () => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('lead_id')) {
            return urlParams.get('lead_id')
        }
    }

    // clearRates: the user confirmed dropping what was entered for the current program.
    const saveQuote = (quote, clearRates = false) => {
        setSelectedProgramCode(quote.program_code)
        setSelectedTerm(quote.term)
        setSelectedLender(quote.lender_name)
        setSelectedTier(quote.tier)

        // Persist a credit score to the Deal on selection. If the picked quote
        // requires sub-780 (below-T1) credit, save its band top (max_credit_score)
        // so the Deal reflects the credit the quote actually qualifies for;
        // otherwise fall back to the Credit Score filter value. Blank clears it.
        const filterCreditScore = (creditScore === '' || creditScore == null) ? null : Number(creditScore)
        const quoteMaxCreditScore = Number(quote.max_credit_score)
        const creditScoreValue = (!isNaN(quoteMaxCreditScore) && quoteMaxCreditScore < 780)
            ? quoteMaxCreditScore
            : filterCreditScore

        let updateData = {
            ...deskingState,
            term: quote.term,
            lender: quote.lender_name,
            tier: quote.tier,
            program_code: quote.program_code,
            // This request only: tells the backend the program was picked here.
            program_selected: true,
            credit_score: creditScoreValue,
            // pricing_id / pricing must come AFTER ...deskingState — the desking
            // response can carry its own (null/stale) pricing_id that would
            // otherwise clobber the one we want to link the new Deal to.
            pricing_id: pricing_id,
            pricing: pricing_id,
        }

        if (dealId) {
            if (dealState) {
                let newDealState = structuredClone(dealState)

                // The backend rescan prices with the rebates marked is_applied on
                // formatted_quote.rebates, so match them to the rebates the grid
                // priced this quote with. Custom rebates are left as they are.
                const quoteRebateIds = (quote.applied_rebate_ids || []).map(String)
                if (quoteRebateIds.length && newDealState.formatted_quote?.rebates) {
                    newDealState.formatted_quote.rebates = newDealState.formatted_quote.rebates.map((rebate) => (
                        rebate.is_custom
                            ? rebate
                            : { ...rebate, is_applied: quoteRebateIds.includes(String(rebate.external_id)) }
                    ))
                }

                updateData = {
                    pricing_id: pricing_id,
                    ...deskingState,
                    ...newDealState,
                    term: quote.term,
                    lender: quote.lender_name,
                    tier: quote.tier,
                    program_code: quote.program_code,
                    program_selected: true,
                    credit_score: creditScoreValue,
                    desired_payment: null,  // when desking many quotes it is impossible to force a payment
                    ...(clearRates ? PROGRAM_SWITCH_CLEARED : {}),
                }
            }
            return updateDeal(dealId, updateData).then((data) => {
                const responseData = data?.data
                closeModalById("DeskingModal")
                // The DealPage merges this into its dealState. Carry the credit
                // score we just persisted so the page reflects it even if the
                // response payload doesn't echo the updated value.
                onSave && onSave(responseData ? { ...responseData, credit_score: creditScoreValue } : responseData)
            }).catch(() => {})
        } else {
            if (getLeadId()) {
                updateData['leads'] = [getLeadId()]
            }
            // pricing_id / pricing set last so neither deskingState nor any
            // earlier spread can drop them from the create payload.
            const createDealData = {...deskingState, ...updateData, quotes: null, pricing_id: pricing_id, pricing: pricing_id}

            return createDeal(createDealData).then((data) => {
                const createData = data?.data
                closeModalById("DeskingModal")
                onSave && onSave(createData ? { ...createData, credit_score: creditScoreValue } : createData)
            }).catch(() => {})
        }
    }

    if (!quote) {
        return <td className={'quote empty'}></td>
    }

    // Credit-ineligible quotes are shown but disabled (dimmed, not clickable),
    // with an explanatory tooltip — instead of being filtered out of the grid.
    const creditScoreStr = String(creditScore ?? '').trim()
    const isCreditIneligible = creditScoreStr !== '' && !quotePassesCreditScore(quote, Number(creditScoreStr))

    // Is this the deal's currently-selected quote? program_code uniquely
    // identifies the chosen program, so prefer it (a program can repeat across
    // terms, so also pin the term). Fall back to term + lender + tier only when
    // no program_code is available.
    const hasProgramCode = selectedProgramCode != null && selectedProgramCode !== ''
    let isSelected
    if (hasProgramCode) {
        isSelected = Boolean(
            String(selectedTerm) === String(term) &&
            String(quote.program_code) === String(selectedProgramCode)
        )
    } else {
        // Normalize tiers so "3" / 3 / "T3" compare equal; when the deal carries
        // no tier, match on term + lender alone.
        const selectedTierNorm = normalizeTier(selectedTier)
        const tierMatches =
            !quote.tier ||
            quote.tier === NO_TIER ||
            selectedTierNorm === null ||
            normalizeTier(quote.tier) === selectedTierNorm
        // selectedLender is the raw lender_name, not the column's program label.
        isSelected = Boolean(
            String(selectedTerm) === String(term) &&
            String(selectedLender) === String(quote.lender_name) &&
            tierMatches
        )
    }

    const isLoading = (createDealResponse?.loading || updateDealResponse?.loading)
    const isLoadingAndSelected = isLoading && isSelected

    // Entered rates and residual belong to the program they were typed for:
    // switching to another program asks first, then clears them.
    const switchesRatedProgram = Boolean(dealId && dealState) && hasProgramOverride(dealState) &&
        String(quote.program_code) !== String(dealState.formatted_quote?.program_code ?? '')

    // Cash (one-pay purchase) deals have no financing, so never show an APR.
    const isCashDeal = Boolean(deskingState?.is_purchase && deskingState?.is_one_pay)
    const showApr = isPurchase && !isCashDeal && quote.apr != null && quote.apr !== ''

    return (
        <td className={'quote'}>
            <SkeletonLoader loading={isLoadingAndSelected}>
                <Tooltip
                    arrow
                    placement={'top'}
                    enterDelay={250}
                    // Ineligible quotes get a plain text explanation (default tooltip
                    // chrome); eligible ones keep the rich white summary card.
                    title={
                        isCreditIneligible
                            ? 'Not eligible based on credit score'
                            : <span dangerouslySetInnerHTML={{ __html: copyQuoteLeaseSummaryHTMLTooltip(quote, isDark, showApr) }}/>
                    }
                    slotProps={
                        isCreditIneligible
                            ? { popper: { sx: { zIndex: 11000 } } }
                            : {
                                // Strip MUI's dark tooltip chrome so the summary card
                                // shows on its own; lift the popper above the modal (z 10001).
                                tooltip: { sx: { bgcolor: 'transparent', p: 0, m: 0, maxWidth: 'none', boxShadow: 'none' } },
                                popper: { sx: { zIndex: 11000 } },
                                // Arrow matches the card background so it reads as one piece.
                                arrow: { sx: {color: isDark ? '#1e2435' : '#FFFFFF', boxShadow: 'none', border: 'none' } },
                            }
                    }
                >
                    <span
                        className={classnames('price-chip', {
                            selected: isSelected,
                            'credit-disabled': isCreditIneligible,
                            'lowest-payment': isLowestPayment,
                            'bp-chip': showLender,
                            'apr-chip': showApr,
                        })}
                        onClick={() => {
                            if (isLoading || isCreditIneligible) return
                            if (switchesRatedProgram) setIsConfirmingSwitch(true)
                            else saveQuote(quote)
                        }}
                    >
                        {
                            showLender
                                ? <React.Fragment>
                                    <span className={'bp-amount'}>{quote.monthly_payment.toDollarFormat()}</span>
                                    {showApr && (
                                        <span className={'chip-apr'}>{parseFloat(quote.apr).toFixed(2)}% </span>
                                    )}
                                    <span className={'bp-lender'}>{quote.lender_name}</span>
                                    {programName(quote) && <span className={'bp-program'}>{programName(quote)}</span>}
                                  </React.Fragment>
                                : showApr
                                    ? <React.Fragment>
                                        <span className={'chip-amount'}>{quote.monthly_payment.toDollarFormat()}</span>
                                        <span className={'chip-apr'}>{parseFloat(quote.apr).toFixed(2)}% </span>
                                      </React.Fragment>
                                    : quote.monthly_payment.toDollarFormat()
                        }
                    </span>
                </Tooltip>
            </SkeletonLoader>
            {isConfirmingSwitch && (
                <ConfirmDialog
                    open
                    tone={'primary'}
                    title={'Switch program?'}
                    message={`Switching programs clears the ${isPurchase ? 'APR' : 'rate or money factor, residual'} and Rate Markup entered for the current program.`}
                    confirmLabel={'Switch program'}
                    // Above the Desking modal (z-index 10001).
                    sx={{ zIndex: 10003 }}
                    onCancel={() => setIsConfirmingSwitch(false)}
                    onConfirm={() => {
                        setIsConfirmingSwitch(false)
                        saveQuote(quote, true)
                    }}
                />
            )}
        </td>
    )
}
